/**
 * Estado vacío y estado de error.
 *
 * Requisito del proyecto: si la API falla, la pantalla dice QUÉ ha fallado y ofrece reintentar.
 * Nunca una pantalla en blanco, y nunca una traza de error en bruto.
 */
import { ApiError } from '../api/client';
import { Icon, type IconName } from './Icon';
import { Button } from './Button';
import styles from './EmptyState.module.css';

interface EmptyStateProps {
  title: string;
  body?: string;
  icon?: IconName;
  inline?: boolean;
  actionLabel?: string;
  onAction?: () => void;
}

export function EmptyState({
  title,
  body,
  icon = 'file',
  inline = false,
  actionLabel,
  onAction,
}: EmptyStateProps) {
  return (
    <div className={inline ? `${styles.state} ${styles.inline}` : styles.state}>
      <div className={styles.icon}>
        <Icon name={icon} size={22} />
      </div>
      <div className={styles.title}>{title}</div>
      {body ? <div className={styles.body}>{body}</div> : null}
      {actionLabel && onAction ? (
        <div className={styles.action}>
          <Button variant="secondary" size="sm" onClick={onAction}>
            {actionLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  inline?: boolean;
}

/** Traduce el error a algo que un usuario pueda leer y accionar. */
function describe(error: unknown): { title: string; body: string; detail?: string } {
  if (error instanceof ApiError) {
    if (error.code === 'NETWORK_ERROR') {
      return {
        title: 'No se ha podido conectar con la API',
        body: 'Comprueba que el servicio está en marcha. Con Docker: `docker compose up`.',
      };
    }
    if (error.code === 'TIMEOUT') {
      return {
        title: 'La API no responde',
        body: 'La petición ha superado el tiempo de espera. El servicio puede estar arrancando o sobrecargado.',
        detail: error.message,
      };
    }
    if (error.code === 'NO_SEEDED_DATA') {
      return {
        title: 'No hay datos cargados',
        body: 'La base de datos está vacía. Ejecuta la siembra para cargar los datos de demostración.',
        detail: error.message,
      };
    }
    if (error.status === 404) {
      return { title: 'No encontrado', body: error.message };
    }
    return {
      title: 'La API ha devuelto un error',
      body: 'Vuelve a intentarlo. Si persiste, revisa los registros de la API.',
      detail: `${error.code}: ${error.message}`,
    };
  }
  return {
    title: 'Algo ha ido mal',
    body: 'Se ha producido un error inesperado al mostrar esta pantalla.',
    detail: error instanceof Error ? error.message : undefined,
  };
}

export function ErrorState({ error, onRetry, inline = false }: ErrorStateProps) {
  const { title, body, detail } = describe(error);
  return (
    <div className={inline ? `${styles.state} ${styles.inline}` : styles.state} role="alert">
      <div className={`${styles.icon} ${styles.iconError}`}>
        <Icon name="bell" size={22} />
      </div>
      <div className={styles.title}>{title}</div>
      <div className={styles.body}>{body}</div>
      {detail ? <div className={styles.detail}>{detail}</div> : null}
      {onRetry ? (
        <div className={styles.action}>
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Reintentar
          </Button>
        </div>
      ) : null}
    </div>
  );
}
